import { useState, useEffect, useRef } from 'react';
import textStatistics from 'text-statistics';
import TextFieldToCompare from '../../components/TextFieldToCompare';
import CompareButton from '../../components/CompareButton';
import CompareResults from '../../components/CompareResults';
import ErrorMessage from '../../components/ErrorMessage';
import { Grid, Box } from '@mui/material';
import DiffMatchPatch from 'diff-match-patch';
import Sentiment from 'sentiment';
import { Text1Scan } from '../../stores/compareScans/Text1Scan';
import { Text2Scan } from '../../stores/compareScans/Text2Scan';
import { useStoreState } from 'pullstate';
import {
    findTop3RepeatedWords,
    calculateContentAnalytics,
} from '../../utils/helpers';

const dmp = new DiffMatchPatch();

const Compare2 = () => {
    const { content: text1Content } = useStoreState(Text1Scan);
    const { content: text2Content } = useStoreState(Text2Scan);
    const [text1, setText1] = useState(text1Content);
    const [text2, setText2] = useState(text2Content);
    const [similarity, setSimilarity] = useState(null);
    const [showCompareResults, setShowCompareResults] = useState(false);
    const [error, setError] = useState(null);
    const [showError, setShowError] = useState(false);
    const [empty1, setEmpty1] = useState(false);
    const [empty2, setEmpty2] = useState(false);
    const [isCompared, setIsCompared] = useState(false);
    const [uploadCompleted, setUploadCompleted] = useState({ text1: false, text2: false });
    //anayltics
    const [text1Analytics, setText1Analytics] = useState({});
    const [text2Analytics, setText2Analytics] = useState({});
    const [text1Sentiment, setText1Sentiment] = useState({});
    const [text2Sentiment, setText2Sentiment] = useState({});
    const [text1TopWords, setText1TopWords] = useState([]);
    const [text2TopWords, setText2TopWords] = useState([]);

    const resultsRef = useRef(null);

    const tabName1 = 'Text 1';
    const tabName2 = 'Text 2';

    useEffect(() => {
        let timeout;
        if (showCompareResults && resultsRef.current) {
            timeout = setTimeout(() => {
                resultsRef.current.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [showCompareResults]);

    const handleCompareClick = () => {
        const isText1Empty = !text1.trim();
        const isText2Empty = !text2.trim();
        setEmpty1(isText1Empty);
        setEmpty2(isText2Empty);

        if (isText1Empty || isText2Empty) {
            setError('Both fields must have text to compare.');
            setShowError(true);
            setShowCompareResults(false);
            // setSimilarity(null);
            return;
        }
        setError(null);
        setShowError(false);

        const diffs = dmp.diff_main(text1, text2);
        dmp.diff_cleanupSemantic(diffs);
        let commonTextLength = 0;
        diffs.forEach((diff) => {
            if (diff[0] === 0) commonTextLength += diff[1].length;
        });

        setSimilarity(commonTextLength / ((text1.length + text2.length) / 2));
        setIsCompared(true);
        console.log('Comparison clicked, showing results:', showCompareResults);
        setShowCompareResults(true);

        // Compute analytics for Text1
        const sentimentAnalyzer = new Sentiment();
        const sentimentResult1 = sentimentAnalyzer.analyze(text1);
        const readabilityScore1 = new textStatistics(text1).fleschKincaidReadingEase();
        setText1Sentiment(sentimentResult1);
        setText1TopWords(findTop3RepeatedWords(text1));
        setText1Analytics({
            ...calculateContentAnalytics(text1),
            readabilityScore: readabilityScore1,
        });

        // Compute analytics for Text2
        const sentimentResult2 = sentimentAnalyzer.analyze(text2);
        const readabilityScore2 = new textStatistics(text2).fleschKincaidReadingEase();
        setText2Sentiment(sentimentResult2);
        setText2TopWords(findTop3RepeatedWords(text2));
        setText2Analytics({
            ...calculateContentAnalytics(text2),
            readabilityScore: readabilityScore2,
        });
    };

    const handleCloseResults = () => {
        setShowCompareResults(false);
    };

    const handleUploadComplete = (field) => {
        setUploadCompleted((prev) => ({ ...prev, [field]: true }));
    };

    const handleText1Change = (event) => {
        setText1(event.target.value);
        setEmpty1(false);
    };

    const handleText2Change = (event) => {
        setText2(event.target.value);
        setEmpty2(false);
    };

    const handleResetText1 = () => {
        setText1('');
        setEmpty1(false);
    };

    const handleResetText2 = () => {
        setText2('');
        setEmpty2(false);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: 0.2,
                    marginTop: 2,
                }}
            >
                <CompareButton onCompare={handleCompareClick} />
            </Box>
            <Grid container spacing={2} justifyContent='center' sx={{ minWidth: '100%' }}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextFieldToCompare
                        tabName={tabName1}
                        value={text1}
                        onChange={handleText1Change}
                        isCompared={isCompared}
                        onClear={handleResetText1}
                        CurrentScan={Text1Scan}
                        error={empty1}
                        sx={{ flex: 1 }}
                        onUploadComplete={() => handleUploadComplete('text1')}
                        uploadCompleted={uploadCompleted.text1}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <TextFieldToCompare
                        tabName={tabName2}
                        value={text2}
                        onChange={handleText2Change}
                        isCompared={isCompared}
                        onClear={handleResetText2}
                        CurrentScan={Text2Scan}
                        error={empty2}
                        sx={{ flex: 1 }}
                        onUploadComplete={() => handleUploadComplete('text2')}
                        uploadCompleted={uploadCompleted.text2}
                    />
                </Grid>
                {showError && (
                    <Grid item xs={12} md={8}>
                        <ErrorMessage
                            open={showError}
                            message={error}
                            onClose={() => setShowError(false)}
                        />
                    </Grid>
                )}
                {showCompareResults && (
                    <Grid item xs={12} md={8} ref={resultsRef}>
                        <CompareResults
                            show={showCompareResults}
                            similarity={similarity}
                            text1Analytics={text1Analytics}
                            text2Analytics={text2Analytics}
                            text1Sentiment={text1Sentiment}
                            text2Sentiment={text2Sentiment}
                            text1TopWords={text1TopWords}
                            text2TopWords={text2TopWords}
                            onClose={handleCloseResults}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default Compare2;
